// client.ts — R-06
// Instance axios configurée avec intercepteurs pour l'authentification MSAL
// et le mapping des erreurs ProblemDetails RFC 7807 → messages français.
// Dual-auth (004): fallback JWT local depuis sessionStorage si MSAL non authentifié.
// Configuration commune (baseURL/headers/timeout) et mapping d'erreurs partagés
// via http-client-factory.ts — voir aussi local-auth-client.ts.

import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";

import {
  AUTH_EXPIRED_EVENT,
  AUTH_METHOD_KEY,
  LOCAL_AUTH_TOKEN_KEY,
} from "@/domain/constants/auth-constants";
import { ApiError } from "@/infrastructure/adapters/problem-details";
import {
  attachProblemDetailsInterceptor,
  createHttpClient,
} from "@/infrastructure/api/http-client-factory";
import {
  AuthRedirectInProgressError,
  getAccessToken,
} from "@/infrastructure/msal/auth-helpers";

const apiClient: AxiosInstance = createHttpClient();

function notifyAuthExpired(): void {
  if ("window" in globalThis) {
    globalThis.window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  }
}

// Request interceptor: attach only the token for the selected provider.
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const selectedAuthMethod = sessionStorage.getItem(AUTH_METHOD_KEY);
    const localToken = sessionStorage.getItem(LOCAL_AUTH_TOKEN_KEY);

    // Keep provider tokens isolated when both sessions exist in one browser tab.
    if (selectedAuthMethod !== "microsoft" && localToken) {
      config.headers.Authorization = `Bearer ${localToken}`;
      return config;
    }

    if (selectedAuthMethod === "email") {
      notifyAuthExpired();
      throw new ApiError(401, "Session expirée. Reconnectez-vous.");
    }

    // Microsoft provider selected, or no local session available.
    try {
      const msalToken = await getAccessToken();
      config.headers.Authorization = `Bearer ${msalToken}`;
      return config;
    } catch (error) {
      if (error instanceof AuthRedirectInProgressError) {
        throw error;
      }

      // MSAL unavailable — the request has no usable provider token.
    }
    notifyAuthExpired();
    throw new ApiError(401, "Session expirée. Reconnectez-vous.");
  },
  (error: unknown) => Promise.reject(error),
);

// Intercepteur response partagé : mappe les erreurs → ApiError (401 compris —
// AuthContext lit ensuite `error.status` pour déclencher la redirection login).
attachProblemDetailsInterceptor(apiClient, notifyAuthExpired);

export default apiClient;
